/**
 * useDeliverySLA — business-logic hook for the Delivery SLA UI.
 *
 * Owns everything that is *not* rendering: mapping the raw response (memoised),
 * resolving the initially-selected option, tracking the user's selection, and
 * deriving loading / empty / error / not-serviceable states. Components consume
 * the returned view state and dispatch selection via `selectOption`.
 */
import { useCallback, useMemo, useState } from 'react';
import { SLA_CONTEXT, SlaContext } from './constants';
import { mapSlaOptions } from './mapDeliverySLA';
import { PriceTypeTags, RawSlaOption, SlaCodeLabels, SlaOption, SlaViewModel } from './types';

export type SlaStatus = 'loading' | 'error' | 'empty' | 'not-serviceable' | 'ready';

interface UseDeliverySLAArgs {
  /** Raw option array for the selected SKU. Ignored if `viewModel` is given. */
  slaOptions?: RawSlaOption[] | null;
  /** Pre-mapped view model (e.g. mapped in a selector). Takes precedence. */
  viewModel?: SlaViewModel | null;
  isLoading?: boolean;
  error?: boolean;
  context?: SlaContext;
  priceTypeTags?: PriceTypeTags;
  /** deliverySLA -> copy map (multiSellerSlaConfig.slaTimeMessage). */
  slaCodeLabels?: SlaCodeLabels;
  onSelectOption?: (option: SlaOption) => void;
}

interface UseDeliverySLAResult {
  status: SlaStatus;
  viewModel: SlaViewModel;
  isMultiSla: boolean;
  selectedId: string;
  selectOption: (option: SlaOption) => void;
}

const resolveInitialSelection = (viewModel: SlaViewModel): string => {
  const preselected = viewModel.options.find(option => option.isSelected && option.serviceable);
  if (preselected) return preselected.id;
  const firstServiceable = viewModel.options.find(option => option.serviceable);
  return firstServiceable?.id || '';
};

export const useDeliverySLA = ({
  slaOptions,
  viewModel: externalViewModel,
  isLoading = false,
  error = false,
  context = SLA_CONTEXT.PDP,
  priceTypeTags,
  slaCodeLabels,
  onSelectOption
}: UseDeliverySLAArgs): UseDeliverySLAResult => {
  // Map once per input change; the mapping layer stays the single source of truth.
  const viewModel = useMemo<SlaViewModel>(
    () => externalViewModel || mapSlaOptions(slaOptions, context, priceTypeTags, slaCodeLabels),
    [externalViewModel, slaOptions, context, priceTypeTags, slaCodeLabels]
  );

  const initialSelectedId = useMemo(() => resolveInitialSelection(viewModel), [viewModel]);

  /**
   * Identity of the data the cards are rendering. Every scopedView response
   * (size change, pincode change) lands a fresh option array in the store, so a
   * change here means "new API answer" — including a new `isSelected`.
   */
  const dataToken: unknown = externalViewModel || slaOptions || null;
  const [userSelection, setUserSelection] = useState<{ token: unknown; id: string } | null>(
    null
  );

  /**
   * The user's choice wins, but only over the response it was made on and only
   * while it still points at a valid option. On a new response the API's
   * `isSelected` takes over again — otherwise a manual pick on the previous size
   * would keep the old card highlighted while redux (selectedSlaOption, set on
   * V2 SUCCESS) already moved to the newly-selected option.
   */
  const selectedId = useMemo(() => {
    if (
      userSelection &&
      userSelection.token === dataToken &&
      viewModel.options.some(option => option.id === userSelection.id)
    ) {
      return userSelection.id;
    }
    return initialSelectedId;
  }, [userSelection, dataToken, viewModel, initialSelectedId]);

  const selectOption = useCallback(
    (option: SlaOption) => {
      if (!option?.serviceable || option.id === selectedId) return;
      setUserSelection({ token: dataToken, id: option.id });
      onSelectOption?.(option);
    },
    [onSelectOption, selectedId, dataToken]
  );

  const status: SlaStatus = useMemo(() => {
    if (isLoading) return 'loading';
    if (error) return 'error';
    if (viewModel.options.length === 0) return 'empty';
    if (!viewModel.serviceable) return 'not-serviceable';
    return 'ready';
  }, [isLoading, error, viewModel]);

  return {
    status,
    viewModel,
    isMultiSla: viewModel.isMultiSla,
    selectedId,
    selectOption
  };
};

export default useDeliverySLA;
